import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@hqo/ui-atoms';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MboModalState } from 'app/shared/hooks/use-mbo-widget-modal';

interface MboWidgetModalProps {
  state: MboModalState;
  onClose: () => void;
  onResumeCart: () => void;
  onReopenSignin: () => void;
}

/**
 * Renders the Mindbody cart inside our own dialog rather than Mindbody's overlay.
 *
 * The frame is intentionally NOT sandboxed: the cart runs its own booking flow and needs its
 * scripts, forms and cookies. Sign-in is the one page that cannot be framed
 * (`X-Frame-Options: SAMEORIGIN`), so it happens in a separate window and we return the user
 * to the cart afterwards — see `useMboWidgetModal`.
 */
export const MboWidgetModal = ({
  state,
  onClose,
  onResumeCart,
  onReopenSignin,
}: MboWidgetModalProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  // Mindbody localises its own trigger labels, so prefer the label the user just clicked and
  // fall back to our translated title when the trigger has no text of its own.
  const title = (state.kind !== 'closed' && state.label) || formatMessage({ id: 'mindbody.modal.title' });

  return (
    <Dialog open={state.kind !== 'closed'} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="mbo-widget-modal"
        className="flex h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
      >
        <DialogHeader className="border-b border-border-border-secondary px-6 py-4 pr-12">
          <DialogTitle className="truncate text-base">{title}</DialogTitle>
        </DialogHeader>

        {state.kind === 'cart' && (
          // Keyed on the URL so returning from sign-in loads a fresh frame rather than
          // reusing the pre-sign-in one.
          <iframe
            key={state.url}
            src={state.url}
            title={title}
            data-testid="mbo-widget-modal-frame"
            className="min-h-0 w-full flex-1 border-0"
          />
        )}

        {state.kind === 'signin' && (
          <div
            data-testid="mbo-widget-modal-signin"
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center"
          >
            <p className="text-base font-semibold">
              <FormattedMessage id="mindbody.modal.signinTitle" />
            </p>
            <p className="text-sm text-text-tertiary">
              <FormattedMessage id="mindbody.modal.signinDescription" />
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <Button onClick={onResumeCart}>
                <FormattedMessage id="mindbody.modal.signinDone" />
              </Button>
              <Button variant="outline" onClick={onReopenSignin}>
                <FormattedMessage id="mindbody.modal.signinReopen" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
